/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

/*
 * Local edits not included in the shadcn registry. Restore them after
 * `yarn shadcn:add dialog --overwrite`:
 *
 * - `DialogContent` is at most the viewport's height minus `--spacing(8)`. It has a
 *   row for the header and a body row that can shrink (`grid-rows-[auto_minmax(0,1fr)]`).
 *   A footer gets an implicit `auto` row.
 * - `DialogContent` holds its column to the dialog's width (`grid-cols-1`), so a long
 *   word can't widen every row.
 * - `DialogContent` gives a direct child form `display: contents`, so one form can
 *   wrap both `DialogBody` and `DialogFooter`.
 * - `DialogBody` is new. It scrolls, with room for focus rings at its edges.
 */

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { XIcon } from "lucide-react"
import type * as React from "react"

import { Button } from "@/ui/primitives/button"
import { cn } from "@/utils/cn"

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
    return (
        <DialogPrimitive.Backdrop
            data-slot="dialog-overlay"
            className={cn(
                "fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
                className
            )}
            {...props}
        />
    )
}

/**
 * Dialog popup, at most the viewport's height minus `--spacing(8)`. It stays
 * centered, so that leaves `--spacing(4)` above and below, the same as the
 * gutter at each side.
 *
 * Its children stack as the header, the body and an optional footer, in that
 * order. The second row is the one that shrinks, so a `DialogBody` there scrolls
 * when the content doesn't fit. Anything placed between the header and the body
 * would take that row instead.
 *
 * A form that is a direct child gets `display: contents`, so its `DialogBody`
 * and `DialogFooter` lay out as the dialog's own children. The footer stays
 * inside the form, so its submit button still submits it.
 *
 * @example
 * <DialogContent>
 *     <DialogHeader>…</DialogHeader>
 *     <Form of={form} onSubmit={submit}>
 *         <DialogBody>…fields…</DialogBody>
 *         <DialogFooter>…buttons…</DialogFooter>
 *     </Form>
 * </DialogContent>
 */
function DialogContent({
    className,
    children,
    showCloseButton = true,
    ...props
}: DialogPrimitive.Popup.Props & {
    showCloseButton?: boolean
}) {
    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Popup
                data-slot="dialog-content"
                className={cn(
                    "fixed top-1/2 left-1/2 z-50 grid max-block-[calc(100dvb-(--spacing(8)))] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] gap-6 rounded-xl bg-popover p-6 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-md data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 [&>form]:contents",
                    className
                )}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        render={<Button variant="ghost" className="absolute top-4 right-4" size="icon-sm" />}
                    >
                        <XIcon />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Popup>
        </DialogPortal>
    )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
    return <div data-slot="dialog-header" className={cn("flex flex-col gap-2", className)} {...props} />
}

/**
 * Scrolling area between the header and the footer.
 *
 * A scrolling box clips whatever sticks out of it, focus rings included. So the
 * body reaches into the dialog's side padding and the gaps above and below
 * (`-mx-6 -my-1`), then pads the same amount back. `-mx-6` matches
 * `DialogContent`'s `p-6`, so change them together.
 */
function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
    return <div data-slot="dialog-body" className={cn("-mx-6 -my-1 overflow-y-auto px-6 py-1", className)} {...props} />
}

function DialogFooter({
    className,
    showCloseButton = false,
    children,
    ...props
}: React.ComponentProps<"div"> & {
    showCloseButton?: boolean
}) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
            {...props}
        >
            {children}
            {showCloseButton && (
                <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
            )}
        </div>
    )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn("font-heading leading-none font-medium", className)}
            {...props}
        />
    )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn(
                "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
                className
            )}
            {...props}
        />
    )
}

export {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger
}
